import { Link } from "@tanstack/react-router";

import { Footer } from "./Footer";
import { Header } from "./Header";
import { Typewriter } from "./Typewriter";
import { SpecialOffer } from "../home/SpecialOffer";
import { SITE_URL } from "../../lib/site";

const defaultTicker = [
  "ошибка 404",
  "страница не найдена",
  "но мы уже чиним",
  "а пока — наши кейсы",
  "hay.agency",
];

type LinkTo = "/" | "/cases" | "/blog" | "/contacts";

type Props = {
  eyebrow?: string;
  slogans: [string, string][];
  description: string;
  primary?: { label: string; to: LinkTo };
  secondary?: { label: string; to: LinkTo };
  ticker?: string[];
  jsonLdName?: string;
};

/**
 * Креативная страница 404 — используется и для несуществующих URL,
 * и для несуществующих статей блога.
 */
export function NotFoundView({
  eyebrow = "страница не найдена",
  slogans,
  description,
  primary = { label: "На главную", to: "/" },
  secondary = { label: "Наши кейсы", to: "/cases" },
  ticker = defaultTicker,
  jsonLdName = "Страница не найдена — hay.agency",
}: Props) {
  const words = [...ticker, ...ticker, ...ticker];

  return (
    <div className="flex min-h-screen flex-col bg-background font-sans text-foreground antialiased">
      <SpecialOffer />
      <Header />

      <main className="relative flex flex-1 flex-col justify-center overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 select-none whitespace-nowrap text-center text-[38vw] font-bold leading-none tracking-[-0.06em] text-foreground/[0.05] lg:text-[24vw]"
        >
          404
        </div>

        <div className="relative px-5 py-16 lg:px-[21px] lg:py-24">
          <div className="flex items-center gap-3">
            <span className="pulse-dot h-[10px] w-[10px] rounded-full bg-brand" />
            <span className="label-caps text-muted-foreground">{eyebrow}</span>
          </div>

          <Typewriter
            as="h1"
            slogans={slogans}
            className="mt-8 text-[44px] font-medium leading-[46px] tracking-[-1.8px] lg:mt-[46px] lg:text-[96px] lg:leading-[98px] lg:tracking-[-3px]"
          />

          <p className="body-12 mt-6 max-w-[420px] text-muted-foreground lg:mt-8">
            {description}
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row lg:mt-[56px]">
            <Link
              to={primary.to}
              className="btn-label flex h-[53px] items-center justify-center bg-brand px-10 text-brand-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
            >
              {primary.label}
            </Link>
            <Link
              to={secondary.to}
              className="btn-label flex h-[53px] items-center justify-center bg-secondary px-10 transition-colors duration-300 hover:bg-brand hover:text-brand-foreground"
            >
              {secondary.label}
            </Link>
          </div>
        </div>

        <div className="relative -mx-4 -rotate-2 border-y border-border bg-secondary py-3 lg:-mx-6">
          <div className="marquee overflow-hidden">
            <div className="marquee-track items-center gap-8">
              {words.map((word, i) => (
                <span
                  key={`${word}-${i}`}
                  className="label-caps flex shrink-0 items-center gap-8 whitespace-nowrap text-muted-foreground"
                >
                  {word}
                  <span className="h-[6px] w-[6px] rounded-full bg-brand" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: jsonLdName,
            url: `${SITE_URL}/404`,
            isPartOf: { "@type": "WebSite", name: "hay.agency", url: SITE_URL },
          }),
        }}
      />
    </div>
  );
}
